import { Answer, DraftSection, FreshnessFinding } from "../types";

/**
 * Pure code scanner detecting outdated calendar years cited in library answers
 */
export function scanStaleYears(
  answers: Answer[],
  thresholdYears: number = 2,
  referenceYear: number = new Date().getFullYear(),
): FreshnessFinding[] {
  const cutoffYear = referenceYear - thresholdYears;
  const findings: FreshnessFinding[] = [];
  const yearRegex = /\b(19\d{2}|20\d{2})\b/g;

  answers.forEach((ans) => {
    // Only scan active or review_due answers
    if (ans.status === "retired") return;

    const fullText = `${ans.title} ${ans.body}`;
    const matches = fullText.match(yearRegex);
    if (!matches) return;

    const staleYearsFound = Array.from(
      new Set(
        matches
          .map((m) => parseInt(m, 10))
          .filter((y) => !isNaN(y) && y <= cutoffYear && y >= 1990),
      ),
    ).sort((a, b) => a - b);

    if (staleYearsFound.length > 0) {
      const yearList = staleYearsFound.join(", ");
      findings.push({
        id: `stale-year-${ans.id}`,
        type: "stale_year",
        severity: "medium",
        targetType: "library_answer",
        targetId: ans.id,
        targetTitle: ans.title,
        details: `Cites year(s) [${yearList}] in answer content, which is ${thresholdYears}+ years older than reference year ${referenceYear}.`,
        suggestedAction: "Review and refresh historical metrics, statistics, or date references.",
        detectedAt: new Date().toISOString(),
      });
    }
  });

  return findings;
}

/**
 * Pure code scanner detecting retired or overdue library answers used in draft sections
 */
export function scanDraftSectionsForStaleAnswers(
  draftSections: DraftSection[],
  answers: Answer[],
  opportunities?: Array<{ id: string; title: string }>,
): FreshnessFinding[] {
  const answersMap = new Map<string, Answer>(answers.map((a) => [a.id, a]));
  const oppMap = new Map<string, string>((opportunities || []).map((o) => [o.id, o.title]));

  const findings: FreshnessFinding[] = [];
  const now = new Date();

  draftSections.forEach((section) => {
    if (!section.matchedAnswerIds || section.matchedAnswerIds.length === 0) return;

    const oppTitle = oppMap.get(section.opportunityId) || "Proposal";

    section.matchedAnswerIds.forEach((ansId) => {
      const matchedAns = answersMap.get(ansId);
      if (!matchedAns) return;

      if (matchedAns.status === "retired") {
        findings.push({
          id: `retired-in-draft-${section.id}-${ansId}`,
          type: "retired_answer",
          severity: "high",
          targetType: "draft_section",
          targetId: section.id,
          targetTitle: section.title,
          opportunityId: section.opportunityId,
          opportunityTitle: oppTitle,
          details: `Draft section incorporates retired library answer "${matchedAns.title}".`,
          suggestedAction: "Replace with active verified content or remove outdated citation.",
          detectedAt: new Date().toISOString(),
        });
      } else if (
        matchedAns.status === "review_due" ||
        (matchedAns.reviewDueDate && new Date(matchedAns.reviewDueDate) < now)
      ) {
        findings.push({
          id: `review-due-in-draft-${section.id}-${ansId}`,
          type: "review_due_answer",
          severity: "medium",
          targetType: "draft_section",
          targetId: section.id,
          targetTitle: section.title,
          opportunityId: section.opportunityId,
          opportunityTitle: oppTitle,
          details: `Draft section incorporates library answer "${matchedAns.title}" which is overdue for review.`,
          suggestedAction: "Confirm response validity with the answer owner before submitting.",
          detectedAt: new Date().toISOString(),
        });
      }
    });
  });

  return findings;
}

/**
 * Pure code scanner detecting library answers owned by inactive or unassigned staff
 */
export function scanInactiveStaffOwners(
  answers: Answer[],
  activeStaffEmails: string[],
): FreshnessFinding[] {
  const lowerActiveEmails = new Set(activeStaffEmails.map((e) => e.trim().toLowerCase()));
  const findings: FreshnessFinding[] = [];

  answers.forEach((ans) => {
    if (ans.status === "retired") return;

    const ownerEmail = (ans.ownerEmail || "").trim().toLowerCase();
    const isOwnerActive = ownerEmail && lowerActiveEmails.has(ownerEmail);

    if (!isOwnerActive) {
      const ownerDisplay = ans.owner
        ? `${ans.owner} (${ans.ownerEmail || "no email"})`
        : ans.ownerEmail || "Unassigned";
      findings.push({
        id: `inactive-owner-${ans.id}`,
        type: "inactive_owner",
        severity: "low",
        targetType: "library_answer",
        targetId: ans.id,
        targetTitle: ans.title,
        details: `Answer owner "${ownerDisplay}" is not active in MLC Staff HQ.`,
        suggestedAction: "Reassign answer ownership to an active team member.",
        detectedAt: new Date().toISOString(),
      });
    }
  });

  return findings;
}

/**
 * Comprehensive freshness scanner aggregating all non-AI deterministic code checks
 */
export function runComprehensiveFreshnessScan(params: {
  answers: Answer[];
  draftSections?: DraftSection[];
  activeStaffEmails?: string[];
  thresholdYears?: number;
  referenceYear?: number;
  opportunities?: Array<{ id: string; title: string }>;
}): FreshnessFinding[] {
  const {
    answers,
    draftSections = [],
    activeStaffEmails = [],
    thresholdYears = 2,
    referenceYear,
    opportunities = [],
  } = params;

  const staleYearFindings = scanStaleYears(answers, thresholdYears, referenceYear);
  const draftAnswerFindings = scanDraftSectionsForStaleAnswers(
    draftSections,
    answers,
    opportunities,
  );
  const inactiveOwnerFindings =
    activeStaffEmails.length > 0 ? scanInactiveStaffOwners(answers, activeStaffEmails) : [];

  const combined = [...draftAnswerFindings, ...staleYearFindings, ...inactiveOwnerFindings];

  // Sort: High severity first, then Medium, then Low
  const severityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
  return combined.sort(
    (a, b) => (severityOrder[a.severity] ?? 3) - (severityOrder[b.severity] ?? 3),
  );
}
